import React from "react";
import { Link } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { useSubscription, TierFeatures } from "../contexts/SubscriptionContext";
import AppRouterConfig from "../AppRouterConfig";
import { designTokens } from "../theme/designTokens";

/**
 * Copy shown on the upgrade prompt per gated feature. Falls back to a
 * generic message for flags without bespoke copy.
 */
const FEATURE_COPY: Partial<
	Record<keyof TierFeatures, { title: string; description: string }>
> = {
	scope3ActivityModules: {
		title: "Detailed Scope 3 tracking is not included in your plan",
		description:
			"Business travel, commuting, and transport & distribution tracking are available on higher plans.",
	},
	canRunSimulations: {
		title: "The Decarbonisation Simulator is not included in your plan",
		description:
			"Model reduction strategies and simulate scenarios on higher plans.",
	},
	canGenerateReports: {
		title: "Report generation is not included in your plan",
		description:
			"Generate formatted emissions reports for your company on higher plans.",
	},
};

interface FeatureGateProps {
	feature: keyof TierFeatures;
	children: React.ReactNode;
}

/**
 * Route-level plan gate.
 *
 * Renders children unless the company's plan is KNOWN to exclude the
 * feature. While the subscription is still loading, failed to load, or
 * the API predates a flag (value undefined), children render — the
 * backend enforces every gate authoritatively; this UI lock is a
 * courtesy, so it must never lock someone out by mistake.
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
	feature,
	children,
}) => {
	const { state } = useSubscription();

	const value = state.tierFeatures?.[feature];
	const knownDenied =
		value === false || (typeof value === "number" && value === 0);

	if (!knownDenied) return <>{children}</>;
	return <UpgradePrompt feature={feature} />;
};

const UpgradePrompt: React.FC<{ feature: keyof TierFeatures }> = ({
	feature,
}) => {
	const copy = FEATURE_COPY[feature] ?? {
		title: "This feature is not included in your plan",
		description: "Upgrade your plan to unlock it.",
	};

	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			minHeight="60vh"
			padding={2}
		>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 460,
					padding: "40px 32px",
					textAlign: "center",
					border: "1px solid rgba(0, 23, 28, 0.10)",
					borderRadius: designTokens.borderRadius.lg,
				}}
			>
				<LockOutlinedIcon
					sx={{
						fontSize: "2.5rem",
						color: designTokens.colors.secondary.dark,
						marginBottom: 1,
					}}
				/>
				<Typography variant="h6" gutterBottom>
					{copy.title}
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginBottom: 3 }}
				>
					{copy.description}
				</Typography>
				<Button
					component={Link}
					to={AppRouterConfig.USER.CUSTOMER_PORTAL}
					variant="contained"
					color="secondary"
				>
					View plans & billing
				</Button>
			</Paper>
		</Box>
	);
};

export default FeatureGate;
